import { Box, Button, Typography } from '@mui/material';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import BaseInput from '~/components/common/base-input/BaseInput';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import styles from './Device.module.scss';
import { memo, useEffect, useState } from 'react';
import {
  cast,
  handleApiError,
  isWard,
  isXaphuongTinhThanh,
} from '~/utils/helper';
import { IMasterDataItem } from '~/utils/interface/master';
import locationApi from '~/apis/location';
import BaseRow from '~/components/common/base-row/BaseRow';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { PAGE_INDEX } from '~/utils/constants/common';
import { AddNewIcon, TranferIcon } from '~/assets/icons';
import useDebounce from '~/hooks/useDebounce';

import DeviceList from './components/DeviceList';
import AddImeiDialog from '~/pages/admin/devices/components/add-imei-dialog/AddImeiDialog';
import TransferDeviceDialog from './components/TransferDeviceDialog';
import { deviceActions } from '~/thunks/device/deviceSlice';
import { handleGetDevices } from '~/thunks/device/deviceThunk';

const cx = classNames.bind(styles);

const Device = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/device');

  const { filter, needRefreshData } = useAppSelector((state) => state.device);

  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);

  const currentUser =
    Object.values(selectedUser).length === 0 ? userProfile : selectedUser;

  const canAddDevice = Number(currentUser?.groupid) === 1;
  const canTransferDevice = [1, 3].includes(Number(currentUser?.groupid));

  const [provinceOptions, setProvinceOptions] = useState<IMasterDataItem[]>([]);
  const [wardOptions, setWardOptions] = useState<IMasterDataItem[]>([]);
  const [isOpenAddImeiDialog, setIsOpenAddImeiDialog] = useState<boolean>(false);
  const [isOpenTransferDialog, setIsOpenTransferDialog] = useState(false);
  const [searchInput, setSearchInput] = useState(filter.search);
  const debouncedSearch = useDebounce(searchInput);

  useEffect(() => {
    if (debouncedSearch === filter.search) return;

    dispatch(
      deviceActions.setFilter({
        search: debouncedSearch,
        pageIndex: PAGE_INDEX,
      })
    );
  }, [debouncedSearch, dispatch, filter.search]);

  const statusOptions = [
    {
      id: 1,
      name: t('status.assigned'),
    },
    {
      id: 0,
      name: t('status.unassigned'),
    },
    {
      id: 2,
      name: t('status.broken'),
    },
  ];

  useEffect(() => {
    if (!currentUser?.username) return;
    dispatch(
      handleGetDevices({
        username: cast(currentUser.username),
        provinceId: filter.provinceId || "",
        wardId: filter.wardId || "",
        status: filter.status === '' ? '' : Number(filter.status),
        from: filter.from,
        to: filter.to,
        search: filter.search?.trim(),
        pageIndex: filter.pageIndex,
        pageSize: filter.pageSize,
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder,
      })
    ).catch((error) => {
      handleApiError(error);
    });
  }, [
    dispatch,
    currentUser?.username,
    currentUser?.province_id,
    currentUser?.ward_id,
    filter,
    needRefreshData,
  ]);

  /**
   * Get province list
   */
  useEffect(() => {
    getListProvinces();
  }, []);

  /**
   * Get ward list when province changes
   */
  useEffect(() => {
    const provinceId =
      filter.provinceId || currentUser?.province_id;

    if (!provinceId) {
      setWardOptions([]);
      return;
    }

    const getWards = async () => {
      try {
        const response = await locationApi.getListWards({
          province: cast(provinceId),
        });

        setWardOptions(
          response.data.map((item) => ({
            id: item.code,
            name: item.name,
            data0: item.name,
            data1: item.name_with_type,
            data2: item.type,
            data3: item.parent_code,
          }))
        );
      } catch (error) {
        setWardOptions([]);
        handleApiError(error);
      }
    };

    getWards();
  }, [
    filter.provinceId,
    currentUser?.province_id,
  ]);

  /**
   * Reset filter when leave page
   */
  useEffect(() => {
    return () => {
      dispatch(deviceActions.resetFilter());
      setWardOptions([]);
    };
  }, [dispatch]);

  /**
   * Get provinces
   */
  const getListProvinces = async () => {
    try {
      const response = await locationApi.getListProvinces();

      setProvinceOptions(
        response.data.map((item) => ({
          id: item.code,
          name: item.name,
          data0: item.name,
          data1: item.name_with_type,
          data2: item.type,
        }))
      );
    } catch (error) {
      handleApiError(error);
    }
  };

  /**
   * Clear filters
   */
  const handleClearFilter = () => {
    setSearchInput('');
    dispatch(deviceActions.resetFilter());
  };

  return (
    <Box className={cx('device-screen')}>
      {/* Header */}
      <Box className='flex justify-between items-center bg-[#123B6D] h-20 px-6'>
        <Typography className='text-white font-semibold text-xl'>
          {t('list')}
        </Typography>
        {(canAddDevice || canTransferDevice) && (
          <Box className='flex items-center gap-3'>
            {canTransferDevice && (
              <Button
                className='bg-white rounded-xl text-[#123B6D] h-12 hover:bg-white hover:opacity-80 text-base font-bold px-5'
                startIcon={<TranferIcon />}
                onClick={() => setIsOpenTransferDialog(true)}
              >
                {t('transferDevice')}
              </Button>
            )}
            {canAddDevice && (
              <Button
                className='bg-white rounded-xl text-[#123B6D] h-12 hover:bg-white hover:opacity-80 text-base font-bold px-5'
                startIcon={<AddNewIcon />}
                onClick={() => setIsOpenAddImeiDialog(true)}
              >
                {t('addDevice')}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Filters */}
      <BaseRow className='gap-4 px-6 pt-6 mb-7 !items-end'>
        <Box className='w-full'>
          {/* Province / Ward / Status */}
          <BaseRow className='gap-4 mb-4 max-[1600px]:!flex-col'>
            {/* Search */}
            <Box className='min-[1600px]:w-1/4 w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>
                {t('search')}
              </Typography>
              <BaseInput
                value={searchInput}
                placeholder={t('searchPlaceholder')}
                className={cx('device-search-input')}
                onChange={(event) => setSearchInput(event.target.value)}
                fullWidth
              />
            </Box>

            {/* Province */}
            <Box className='min-[1600px]:w-1/4 w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>
                {t('province_id')}
              </Typography>

              <BaseSelect
                options={
                  isXaphuongTinhThanh(
                    cast(currentUser?.roleid)
                  )
                    ? provinceOptions.filter(
                        (item: IMasterDataItem) =>
                          item.id ===
                          String(currentUser?.province_id)
                      )
                    : provinceOptions
                }
                value={
                  filter.provinceId
                    ? String(filter.provinceId)
                    : String(currentUser?.province_id || '')
                }
                placeholder={t('select_province_id')}
                isDisplayAllOption
                isSearchEnable
                onChange={(e) =>
                  dispatch(
                    deviceActions.setFilter({
                      provinceId: e.target.value,
                      wardId: '',
                      pageIndex: PAGE_INDEX,
                    })
                  )
                }
                disabled={isXaphuongTinhThanh(
                  cast(currentUser?.roleid)
                )}
              />
            </Box>

            {/* Ward */}
            <Box className='min-[1600px]:w-1/4 w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>
                {t('ward_id')}
              </Typography>

              <BaseSelect
                options={wardOptions}
                value={
                  filter.wardId
                    ? String(filter.wardId)
                    : String(currentUser?.ward_id || '')
                }
                placeholder={t('select_ward_id')}
                isDisplayAllOption
                isSearchEnable
                onChange={(e) =>
                  dispatch(
                    deviceActions.setFilter({
                      wardId: e.target.value,
                      pageIndex: PAGE_INDEX,
                    })
                  )
                }
                disabled={isWard(
                  cast(currentUser?.roleid)
                )}
              />
            </Box>

            {/* Status */}
            <Box className='min-[1600px]:w-1/4 w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>
                {t('status.title')}
              </Typography>

              <BaseSelect
                options={statusOptions}
                value={filter.status ?? ''}
                placeholder={t('select_status')}
                isDisplayAllOption
                onChange={(e) => {
                  const value = e.target.value;

                  dispatch(
                    deviceActions.setFilter({
                      status:
                        value === ''
                          ? ''
                          : Number(value),
                      pageIndex: PAGE_INDEX,
                    })
                  );
                }}
              />
            </Box>
          </BaseRow>

          {/* From / To */}
          <BaseRow className='gap-4 max-[1600px]:!flex-col'>
            <Box className='w-full'>
              <Typography className='text-[#161A1F] font-medium text-sm mb-2'>
                {t('createdAt')}
              </Typography>

              <Box className='flex justify-between items-center gap-3 max-[768px]:!flex-col'>
                {/* From */}
                <Box className='w-full'>
                  <BaseDatePicker
                    portalId='datePicker-portalId'
                    value={filter.from}
                    dateFormat='YYYY-MM-DD'
                    onChange={(date) =>
                      dispatch(
                        deviceActions.setFilter({
                          from: date
                            ? dayjs(date)
                                .startOf('day')
                                .format('YYYY-MM-DD')
                            : '',
                          pageIndex: PAGE_INDEX,
                        })
                      )
                    }
                    placeholder={t('from')}
                    calendarProps={
                      filter.to
                        ? {
                            maxDate: dayjs(
                              filter.to
                            ).toDate(),
                          }
                        : {}
                    }
                    className='w-full'
                  />
                </Box>

                <Box className='w-4 h-px bg-black hidden lg:block'></Box>

                {/* To */}
                <Box className='w-full'>
                  <BaseDatePicker
                    portalId='datePicker-portalId'
                    value={filter.to}
                    dateFormat='YYYY-MM-DD'
                    onChange={(date) =>
                      dispatch(
                        deviceActions.setFilter({
                          to: date
                            ? dayjs(date)
                                .endOf('day')
                                .format('YYYY-MM-DD')
                            : '',
                          pageIndex: PAGE_INDEX,
                        })
                      )
                    }
                    placeholder={t('to')}
                    calendarProps={
                      filter.from
                        ? {
                            minDate: dayjs(
                              filter.from
                            ).toDate(),
                          }
                        : {}
                    }
                    className='w-full'
                  />
                </Box>

                {/* Clear */}
                <Box className='flex-1'>
                  <Button
                    className='border border-solid border-[#123B6D] bg-[#DCE8F5] rounded-xl text-base font-bold text-[#123B6D] w-[132px] hover:bg-[#0E2F58] hover:opacity-80 hover:text-white'
                    onClick={handleClearFilter}
                  >
                    {t('clear', {
                      ns: 'admin/common',
                    })}
                  </Button>
                </Box>
              </Box>
            </Box>
          </BaseRow>
        </Box>
      </BaseRow>

      {/* Device list */}
      <Box className='px-6'>
        <DeviceList />
      </Box>

      <AddImeiDialog
        open={isOpenAddImeiDialog}
        setOpen={(open, needRefreshData) => {
          setIsOpenAddImeiDialog(open);
          if (needRefreshData) {
            dispatch(deviceActions.setNeedRefreshData(true));
          }
        }}
      />

      <TransferDeviceDialog
        open={isOpenTransferDialog}
        currentUser={currentUser}
        onClose={() => setIsOpenTransferDialog(false)}
        onTransferred={() => dispatch(deviceActions.setNeedRefreshData(true))}
      />
    </Box>
  );
};

export default memo(Device);